/**
 * Pure pricing helpers for the online booking flow.
 *
 * Package prices are INCLUSIVE of GST (18%) and Event Management Fee (15%).
 * Decomposition:
 *   gst        = 18% of package
 *   afterGst   = 82% of package
 *   emf        = 15% of afterGst
 *   decoration = 85% of afterGst
 *
 * Discount rules:
 *   - Referral code  → 5% off the total (package + add-ons)
 *   - Coupon (flat)  → flat ₹ off
 *   - Coupon (pct)   → percentage off the EMF portion only
 */

export const getBreakdown = (price) => {
  const p = Number(price) || 0;
  const gst = p * 0.18;
  const afterGst = p * 0.82;
  const emf = afterGst * 0.15;
  const decoration = afterGst * 0.85;
  return { decoration, emf, gst };
};

/**
 * Returns the amount (in ₹) to charge at checkout.
 *
 * `opts` is a plain object so the caller can pass current form state cleanly:
 *   - packagePrice (₹)
 *   - addOnsTotal (₹)
 *   - paymentType ("test" | "advance" | "full")
 *   - advanceAmount (₹)
 *   - referralValid (bool)
 *   - couponValid (bool)
 *   - couponDiscount ({ discount_type, discount_amount, discount_percent })
 *   - earlyBirdDiscount (₹) — flat ₹ off if the early-bird promo is active
 */
export const getPayableAmount = ({
  packagePrice,
  addOnsTotal,
  paymentType,
  advanceAmount,
  referralValid,
  couponValid,
  couponDiscount,
  earlyBirdDiscount = 0,
}) => {
  const pkg = Number(packagePrice) || 0;
  const addOns = Number(addOnsTotal) || 0;
  const total = pkg + addOns;
  if (paymentType === "test") return 1;
  let base;
  if (paymentType === "advance") {
    base = Number(advanceAmount) || 0;
  } else {
    base = total;
    if (referralValid) {
      base = Math.round(total * 0.95);
    } else if (couponValid && couponDiscount) {
      const { emf } = getBreakdown(pkg);
      if (couponDiscount.discount_type === "flat") {
        base = Math.max(total - (Number(couponDiscount.discount_amount) || 0), 1);
      } else {
        const emfDiscount = Math.round((emf * (Number(couponDiscount.discount_percent) || 0)) / 100);
        base = total - emfDiscount;
      }
    }
  }
  if (earlyBirdDiscount > 0) {
    base = Math.max(base - earlyBirdDiscount, 1);
  }
  return Math.max(base, 1);
};

/** Returns the ₹ discount the customer is receiving (excludes the early-bird flat discount; that is shown as its own line). */
export const getDiscountAmount = ({
  packagePrice,
  addOnsTotal,
  referralValid,
  couponValid,
  couponDiscount,
}) => {
  const pkg = Number(packagePrice) || 0;
  const addOns = Number(addOnsTotal) || 0;
  const total = pkg + addOns;
  if (referralValid) return Math.round(total * 0.05);
  if (couponValid && couponDiscount) {
    const { emf } = getBreakdown(pkg);
    if (couponDiscount.discount_type === "flat") return Number(couponDiscount.discount_amount) || 0;
    return Math.round((emf * (Number(couponDiscount.discount_percent) || 0)) / 100);
  }
  return 0;
};
